import isEmpty from 'lodash/isEmpty'
import reduce from 'lodash/reduce'
import React, { FC, memo, useMemo } from 'react'

import { useAmplitude } from '@Analytics/hook'
import { ProductDimensions } from '@Components/Product/Dimensions'
import { ProductMaterials } from '@Components/Product/Materials'
import { SpecificationsMultiple } from '@Components/Product/Specifications/Multiple'
import { SpecificationsSingle } from '@Components/Product/Specifications/Single'
import { SpecificationsForProduct } from '@Components/Product/Specifications/types'
import { ProductDisplayDrawerTrigger } from '@Domains/Product/Features/Display/Drawer/Trigger'
import { Product } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

import styles from './ProductSpecifications.module.scss'

interface ProductSpecificationProps {
  product: Product
  selectedVariantID?: string
}

const getProductSpecifications = (product?: Product) => {
  // @ts-ignore - "product.masterVariant.bundleContent" doesn't exist in the auto-generated types, but it's in the query.
  const bundleProducts = product?.masterVariant?.bundleContent?.products?.map(p => p.product) ?? []
  const products = isEmpty(bundleProducts) ? [product] : bundleProducts

  return reduce<Product, SpecificationsForProduct[]>(
    products,
    (arr, p) => {
      if (p?.specifications?.groups && p.specifications.groups.length !== 0) {
        arr.push({
          id: p.id,
          name: p.name || '',
          groups: p.specifications.groups,
        })
      }

      return arr
    },
    [],
  )
}

export const ProductSpecifications: FC<ProductSpecificationProps> = memo(({ product, selectedVariantID }) => {
  const specifications = useMemo(() => getProductSpecifications(product), [product])

  const { t } = useTranslation()
  const ampli = useAmplitude()

  if (specifications.length === 0) {
    return (
      <>
        <ProductDimensions asSection product={product} selectedVariantID={selectedVariantID} />
        <ProductMaterials asSection product={product} selectedVariantID={selectedVariantID} />
      </>
    )
  }

  return (
    <ProductDisplayDrawerTrigger
      onOpenChange={(open) => {
        if (open) {
          ampli.productSpecificationsOpen({
            source_id: 'inherit',
          })
        }
      }}
      sourceID="product_specifications"
      title={t('products:SPECIFICATIONS_HEADER', 'Specifications')}
    >
      <div className={styles.specifications}>
        {specifications.length === 1 && <SpecificationsSingle groups={specifications[0].groups} />}
        {specifications.length > 1 && <SpecificationsMultiple data={specifications} />}
      </div>
    </ProductDisplayDrawerTrigger>
  )
})
